import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Mail, X, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { getGeo } from "../lib/geo";

const DISMISSED_KEY = "haulhub_waitlist_dismissed";
// Full-name lookup for the countries we're most likely to see in the wild.
// If a country isn't here we just show its two-letter ISO code — good enough.
const COUNTRY_NAMES = {
  BR: "Brazil", MX: "Mexico", IN: "India", ID: "Indonesia", NG: "Nigeria",
  ZA: "South Africa", EG: "Egypt", PH: "Philippines", TH: "Thailand",
  VN: "Vietnam", MY: "Malaysia", SG: "Singapore", KR: "South Korea",
  JP: "Japan", CN: "China", GB: "United Kingdom", DE: "Germany", FR: "France",
  IT: "Italy", ES: "Spain", NL: "Netherlands", PL: "Poland", GR: "Greece",
  CA: "Canada", AU: "Australia", NZ: "New Zealand", CH: "Switzerland",
  SE: "Sweden", NO: "Norway", FI: "Finland", DK: "Denmark", CZ: "Czechia",
  RO: "Romania", HU: "Hungary", PT: "Portugal", IE: "Ireland", AT: "Austria",
  BE: "Belgium", UA: "Ukraine", SA: "Saudi Arabia", QA: "Qatar", KW: "Kuwait",
  JO: "Jordan", BH: "Bahrain", OM: "Oman", MA: "Morocco",
  AR: "Argentina", CL: "Chile", CO: "Colombia", KE: "Kenya",
};

/**
 * Shows a dismissable "Coming soon in {country}" banner when the visitor's
 * detected country isn't in Drivons's per-km rate table yet. Collects an email
 * for a waitlist and shows social proof ("142 people on the waitlist") after
 * a successful submit.
 *
 * Renders NOTHING when:
 *   - We haven't detected a country yet
 *   - The visitor is in a supported country (getGeo().supported === true)
 *   - The visitor previously dismissed this banner in the current browser
 */
export default function CountryOnboardingBanner({ source = "banner" }) {
  const { t } = useTranslation();
  const [geo, setGeo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(null); // {waitlist_count, message}

  useEffect(() => {
    setGeo(getGeo());
    try {
      const stored = localStorage.getItem(DISMISSED_KEY);
      if (stored) setDismissed(true);
    } catch { /* private mode → ignore */ }
  }, []);

  if (!geo || geo.supported || dismissed) return null;

  const countryName = COUNTRY_NAMES[geo.country] || geo.country;

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/geo/waitlist", {
        email: email.trim(),
        country: geo.country,
        source,
      });
      setJoined(data);
      toast.success(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — try again.");
    } finally {
      setBusy(false);
    }
  };

  // Success state — brief thank-you card with social proof + close button.
  if (joined) {
    return (
      <div
        className="rounded-2xl bg-gradient-to-br from-electric-teal/20 to-electric-teal/5 border border-electric-teal/40 p-4 relative"
        data-testid="country-waitlist-success"
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-white/40 hover:text-white transition"
          aria-label={t("common.dismiss", "Dismiss")}
          data-testid="country-waitlist-dismiss"
        ><X size={16} /></button>
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase font-black tracking-widest text-electric-teal">
          <Sparkles size={11} /> {t("waitlist.joinedEyebrow", "You're on the list")}
        </div>
        <div className="text-sm text-white mt-1.5" data-testid="country-waitlist-success-message">
          {joined.message}
        </div>
        {joined.waitlist_count > 1 && (
          <div className="text-[11px] text-white/60 mt-1.5" data-testid="country-waitlist-count">
            {t("waitlist.socialProof", "Join {{n}} others in {{country}} waiting for Drivons.", {
              n: joined.waitlist_count,
              country: countryName,
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-amber-500/[0.12] to-amber-500/[0.03] border border-amber-500/30 p-4 relative"
      data-testid="country-onboarding-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-white/40 hover:text-white transition"
        aria-label={t("common.dismiss", "Dismiss")}
        data-testid="country-onboarding-dismiss"
      ><X size={16} /></button>

      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase font-black tracking-widest text-amber-300">
        <Globe size={11} /> {t("waitlist.eyebrow", "Not live in your country yet")}
      </div>
      <h3 className="text-lg font-black text-white mt-1.5" data-testid="country-onboarding-title">
        {t("waitlist.title", "Coming soon to {{country}}", { country: countryName })}
      </h3>
      <p className="text-xs text-white/60 mt-1.5 max-w-sm">
        {t("waitlist.subtitle", "We're expanding fast. Drop your email and we'll ping you the moment Drivons opens in {{country}}.", { country: countryName })}
      </p>

      <form onSubmit={submit} className="mt-3 flex flex-col sm:flex-row gap-2 pr-8 sm:pr-0" data-testid="country-waitlist-form">
        <div className="flex-1 relative">
          <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("waitlist.emailPlaceholder", "your@email.com")}
            className="w-full rounded-full bg-black/40 border border-white/10 pl-8 pr-3 py-2 text-xs text-white placeholder:text-white/40 focus:border-amber-400 outline-none"
            data-testid="country-waitlist-email"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !email.trim()}
          className="rounded-full px-4 py-2 text-xs font-black bg-amber-400 text-obsidian hover:bg-amber-300 transition disabled:opacity-50 inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
          data-testid="country-waitlist-submit"
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          {t("waitlist.submit", "Notify me")}
        </button>
      </form>
    </div>
  );
}
